import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, CampoDeSenha } from "../ui";
import { medirSenha, MINIMO_DE_CARACTERES } from "../../nucleo/senha";

/* Constante de módulo, e não `[]` no parâmetro: um literal novo a cada render invalidaria
   o `useMemo` da força da senha sem que nada tivesse mudado. */
const SEM_FRAGMENTOS: readonly string[] = [];

export interface PropsDoFormularioDeNovaSenha {
  rotuloDoBotao: string;
  rotuloEnviando: string;
  /** Rejeitar com erro faz a mensagem aparecer; o componente não trata a exceção sozinho. */
  aoEnviar: (senha: string) => void;
  enviando: boolean;
  /** Mensagem vinda da API, já traduzida pela página. */
  erro?: string | null;
  /** Trechos do contexto da pessoa que não podem virar senha (e-mail, nome da empresa). */
  evitar?: readonly string[];
}

/**
 * O par senha + confirmação usado no primeiro acesso e na redefinição.
 *
 * As duas telas pedem exatamente a mesma coisa e erram do mesmo jeito; manter uma cópia em
 * cada uma garantiria que uma delas ficasse para trás na primeira mudança de regra.
 */
export function FormularioDeNovaSenha({
  rotuloDoBotao,
  rotuloEnviando,
  aoEnviar,
  enviando,
  erro,
  evitar = SEM_FRAGMENTOS,
}: PropsDoFormularioDeNovaSenha) {
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const forca = useMemo(() => medirSenha(senha, evitar), [senha, evitar]);
  const conferem = confirmacao.length > 0 && senha === confirmacao;

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setErroLocal(null);

    if (!forca.aceitavel) {
      setErroLocal(forca.dica);
      return;
    }
    if (senha !== confirmacao) {
      setErroLocal("As duas senhas não são iguais.");
      return;
    }
    aoEnviar(senha);
  }

  const mensagem = erroLocal ?? erro ?? null;

  return (
    <form onSubmit={enviar} noValidate>
      <CampoDeSenha
        rotulo="Sua nova senha"
        valor={senha}
        aoMudar={(valor) => {
          setSenha(valor);
          setErroLocal(null);
        }}
        autoComplete="new-password"
        forca={forca}
        ajuda={`Pelo menos ${MINIMO_DE_CARACTERES} caracteres. Uma frase que só você saiba vale mais do que um símbolo no fim.`}
        desabilitado={enviando}
        autoFocus
      />

      <CampoDeSenha
        rotulo="Repita a senha"
        valor={confirmacao}
        aoMudar={(valor) => {
          setConfirmacao(valor);
          setErroLocal(null);
        }}
        autoComplete="new-password"
        desabilitado={enviando}
        {...(confirmacao.length > 0 && !conferem
          ? { erro: "As duas senhas não são iguais." }
          : {})}
      />

      {mensagem ? (
        <Aviso tom="erro" className="mb-4" data-testid="erro-da-senha">
          {mensagem}
        </Aviso>
      ) : null}

      <Botao
        type="submit"
        variante="verde"
        larguraTotal
        // O botão só espera o comprimento mínimo e a confirmação. O medidor aconselha, não
        // bloqueia: quem decide se a senha está numa lista de vazadas é a API.
        disabled={enviando || !forca.aceitavel || !conferem}
      >
        {enviando ? rotuloEnviando : rotuloDoBotao}
      </Botao>
    </form>
  );
}
